import { useEffect } from "react";
import { useSelector } from "react-redux";
import { fetchProgramDetails } from "../index";
import { shouldFetchProgramDetails } from "../provider";
import {
  selectActiveLoyaltyProvider,
  selectIsOffline,
  selectLoyaltyProgramDetails,
  selectLoyaltyChannelDefId,
} from "../selectors";

/**
 * Keep the loyalty programme for the selected channel fetched.
 *
 * Fires only when the active provider actually publishes one — asking anyone else is a guaranteed
 * miss — and re-fires on a channel change, because a programme is per channel.
 *
 * `isActive` lets a screen suspend the fetch while it is not the visible one; pass `true` from a
 * surface that is always mounted.
 */
export function useProgramDetailsFetch(channelDefId?: string, isActive = true): void {
  const activeProvider = useSelector(selectActiveLoyaltyProvider);
  const shouldFetch = shouldFetchProgramDetails(activeProvider?.metaId);

  useEffect(() => {
    if (isActive && shouldFetch && channelDefId) fetchProgramDetails(channelDefId);
  }, [isActive, shouldFetch, channelDefId]);
}

/**
 * Fetch the bill's channel's programme (`selectLoyaltyChannelDefId`) if nothing is cached for it
 * yet. On a saved bill that is often NOT the channel `NewOrder` keeps fresh (see the selector).
 *
 * For a surface that NEEDS the programme and can be on screen before the screen that keeps it
 * fresh — each app's `NewOrder`, via `useProgramDetailsFetch` above — has run. `LoyaltyStore` is
 * persisted on neither platform, so the cache starts empty on every launch.
 *
 * 🔴 **Two such surfaces, both found on mobile (2026-09-23), and neither failed visibly** — each
 * rule simply read an empty cache as "no live programme":
 *
 * | caller | reached without `NewOrder` because | what was missing |
 * |---|---|---|
 * | `useCustomerConsent` | the customer step comes BEFORE it: `ChannelSwitch` → `DeliveryDineInModal` → `NewOrder` | the consent block (`requiresLiveProgramForConsent`) |
 * | `useCanRedeemPoints` | the checkout also opens from Table View (`OrdersMB`) and My Orders (`MyOrderCart`) | the points panel (`requiresLiveProgramToRedeem`), so the excluded-payment-mode rule too |
 *
 * Web's main customer modal and its cart's checkout both sit behind `NewOrder`, which is why this
 * surfaced on mobile. Web opens the same checkout from Table View (`KOTView`), My Orders and Sale
 * Return, and the same customer modal from Table View and reservations, none of them behind it.
 *
 * Only on a miss, so a surface opened once the programme has landed costs no second request. A
 * negative answer caches `EMPTY_PROGRAM_DETAILS` and ends it; a failed request writes nothing and
 * is retried on the next channel change or mount, never in a loop. Never offline: the loyalty
 * APIs are server-backed, and a doomed request still raises mobile's global loader. Coming back
 * online fires it.
 */
export function useEnsureProgramDetails(isActive = true): void {
  const channelDefId = useSelector(selectLoyaltyChannelDefId);
  const isCached = useSelector(selectLoyaltyProgramDetails) !== undefined;
  const isOffline = useSelector(selectIsOffline);

  useProgramDetailsFetch(channelDefId, isActive && !isOffline && !isCached);
}
